import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Store } from '../Store';
import { findTabByKey, tabFilterParams } from '../utils/navModel';
import { getTabLabel } from '../utils/labelStore';

/**
 * The filtered menu tab a list screen was opened from (?view=<tab key>, see
 * utils/navModel.js). The list keeps `view` on every URL it builds
 * (viewUrlSuffix), sends the tab's fixed filter to its search endpoint
 * (viewQuerySuffix for query strings, viewParams for params objects) and uses
 * the tab's label as its title.
 *
 * viewPending is true while ?view is set but the tabs have not loaded yet
 * (the Store starts with the static ADMIN_NAV_SECTIONS, which have no _id):
 * lists skip fetching meanwhile so they never flash the unfiltered rows.
 */
export const useListView = () => {
	const { search } = useLocation();
	const {
		state: { tabs },
	} = useContext(Store);
	const viewKey = new URLSearchParams(search).get('view') || '';

	const view = useMemo(() => {
		const viewTab = findTabByKey(tabs, viewKey);
		const tabsLoaded = Array.isArray(tabs) && tabs.some((tab) => tab._id);
		const viewParams = tabFilterParams(viewTab && viewTab.filter);
		const query = new URLSearchParams(viewParams).toString();
		return {
			viewKey,
			viewTab,
			viewParams,
			viewQuerySuffix: query ? `&${query}` : '',
			viewUrlSuffix: viewKey ? `&view=${encodeURIComponent(viewKey)}` : '',
			viewPending: Boolean(viewKey) && !viewTab && !tabsLoaded,
		};
	}, [tabs, viewKey]);
	// Resolved per render so a terminology change re-labels it.
	return { ...view, viewTitle: view.viewTab ? getTabLabel(view.viewTab) : null };
};

export default useListView;
